import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { faClone } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, Typography } from "@mui/material"
import { SelectOrCreateAdvertiserField } from "advertiser/components/SelectOrCreateAdvertiserField"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { getAvailableCurrencyIds } from "currency/tools/common"
import { FieldDate } from "form/components/fields/FieldDate"
import { FieldImpressionCount } from "form/components/fields/FieldImpressionCount"
import { FieldMoney } from "form/components/fields/FieldMoney"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldText } from "form/components/fields/FieldText"
import { Form, Formik } from "formik"
import { messageSVC } from "message/service"
import { IOrderForm, IOrderFormData, IOrderFormSaveFn } from "order/model"
import { onOrderFormSave, validateOrderFormData } from "order/tools/form"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { TestHiddenData } from "ui/components/TestHiddenData"

interface IOrderFormProps {
  form: IOrderForm
  id?: string
  isDspDeal?: boolean
  onSave: IOrderFormSaveFn
  onCancel: () => any
}

export const OrderForm = React.memo(({ form, id, isDspDeal, onSave, onCancel }: IOrderFormProps): JSX.Element => {
  const { isLoading, data, isCreation, isValid, isSaving, hasChanged } = form
  if (isLoading) {
    return <LinearLoader text={translate("order.loading")} />
  }
  return (
    <Formik
      initialValues={data}
      validate={(values: IOrderFormData) => validateOrderFormData(values, isDspDeal, true)}
      onSubmit={(_values, actions) => {
        if (isSaving) {
          return
        }
        onOrderFormSave()
          .then(result => onSave(result))
          .catch(error => {
            console.error(error)
            messageSVC.error(translate("order.error.unknown"))
            actions.setSubmitting(false)
          })
      }}>
      {({ errors, values, isSubmitting, isValidating }) => {
        const canSubmit = !isSubmitting && isValid
        return (
          <Form id={id} style={{ width: "100%", padding: "1rem" }}>
            {form.isValid && <TestHiddenData id="order-form-is-valid" />}
            {isValidating && <TestHiddenData id="order-form-is-validating" />}
            <FlexBox
              wrap
              gap
              fullWidth
              sx={{
                "height": "100%",
                "minHeight": "100%",
                "maxHeight": "100%",
                "& > *": {
                  width: "25rem"
                }
              }}>
              <FieldText
                fieldName="name"
                label={translate("order.name")}
                value={data.name}
                error={errors.name}
                required
                autoFocus
              />
              <FieldText
                fieldName="notes"
                textarea
                label={translate("label.description")}
                value={data.notes}
                error={errors.notes}
              />
              <FlexBox gap>
                <FieldSelect
                  fieldName="budget_currency_id"
                  label={translate("label.currency")}
                  value={data.budget_currency_id}
                  options={getAvailableCurrencyIds()}
                  optionLabelFn={(currency: string) => currency}
                  error={errors.budget_currency_id}
                  sx={{ width: "6rem" }}
                />
                <FieldMoney
                  currencyId={data.budget_currency_id}
                  fieldName="budget"
                  label={translate("label.budget")}
                  value={data.budget}
                  error={errors.budget}
                  placeholder={`ex: ${getFormatedNumber(100000)}`}
                  sx={{ width: "calc(100% - 7rem)" }}
                  clearable
                />
              </FlexBox>
              <FieldImpressionCount
                fieldImpressionName="max_impressions"
                fieldCheckboxName="unlimitedImpressions"
                inputValue={data.max_impressions || 0}
                checkboxValue={data.unlimitedImpressions}
                error={errors.max_impressions}
              />
              <FlexBox vertical gap sx={{ "& > *": { width: "100%" } }}>
                <FieldDate
                  fieldName="start_date"
                  label={translate("label.startDate")}
                  value={values.start_date}
                  error={errors.start_date}
                  toTouchFieldName={"end_date"}
                />
                <FieldDate
                  fieldName={"end_date"}
                  label={translate("label.endDate")}
                  value={values.end_date}
                  error={errors.end_date}
                />
              </FlexBox>
              {!isDspDeal && (
                <SelectOrCreateAdvertiserField value={data.advertiserNameOrId} error={errors.advertiserNameOrId} />
              )}
              {!isDspDeal && typeof data.advertiserNameOrId === "string" && data.advertiserNameOrId !== "" && (
                <FlexBox
                  gap=".5rem"
                  sx={{ "color": theme => theme.palette.info.main, "& .icon": { fontSize: "1.5rem" } }}>
                  <FontAwesomeIcon icon={faCircleInfo} className="icon" />
                  <Typography fontSize=".9rem">
                    {translate("order.add.new.advertiser", data.advertiserNameOrId)}
                  </Typography>
                </FlexBox>
              )}
            </FlexBox>
            <FlexBox gap fullWidth ai="end" jc="end" sx={{ mt: "2rem" }}>
              <Button
                id="order-form-cancel-button"
                variant="outlined"
                onClick={() => onCancel()}
                disabled={form.isSaving}>
                {translate("button.cancel")}
              </Button>
              <Button
                id="order-form-submit-button"
                variant="contained"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress
                      id={id ? `${id}-is-submitting` : "order-form-is-submitting"}
                      sx={{ color: theme => theme.palette.primary.main }}
                      size="1.5rem"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faClone} />
                  )
                }
                type="submit"
                disabled={!hasChanged || !canSubmit || isSaving}>
                {translate(isCreation ? "button.create" : "button.update")}
              </Button>
            </FlexBox>
          </Form>
        )
      }}
    </Formik>
  )
})
